import React, { useState, useCallback } from 'react';
import {
    Box,
    TextField,
    IconButton,
    Tooltip,
    Typography,
    Chip,
    InputAdornment,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import MicOffIcon from '@mui/icons-material/MicOff';
import useRealtimeTranscription from '../../hooks/useRealtimeTranscription';
import features from '../../config/features';

const RecordingIndicator = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
}));

const PulsingDot = styled('span')(({ theme }) => ({
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: theme.palette.error.main,
    animation: 'pulse 1.5s ease-in-out infinite',
    '@keyframes pulse': {
        '0%, 100%': { opacity: 1 },
        '50%': { opacity: 0.4 },
    },
}));

/**
 * DictationTextField - A text field with integrated speech-to-text
 *
 * Combines Material-UI TextField with AssemblyAI real-time transcription
 * Controlled by features.advanced.dictation flag
 *
 * @param {string} value - Current field value
 * @param {function} onChange - Handler for value changes
 * @param {boolean} multiline - Enable multiline input
 * @param {number} rows - Number of visible rows (for multiline)
 * @param {string} label - Field label
 * @param {string} placeholder - Placeholder text
 * @param {boolean} disabled - Disable the field
 * @param {object} sx - Additional styles
 * @param {object} props - Additional TextField props
 */
const DictationTextField = ({
    value = '',
    onChange,
    multiline = true,
    rows = 4,
    label,
    placeholder,
    disabled = false,
    sx = {},
    ...props
}) => {
    const [localError, setLocalError] = useState(null);

    // Check feature flag
    const isDictationEnabled = features.advanced?.dictation ?? true;

    // Handle final transcript - append to existing value
    const handleFinalTranscript = useCallback((transcript) => {
        if (onChange) {
            const newValue = value ? `${value} ${transcript}` : transcript;
            // Create synthetic event matching TextField onChange signature
            const syntheticEvent = {
                target: {
                    value: newValue,
                    name: props.name || '',
                },
            };
            onChange(syntheticEvent);
        }
    }, [value, onChange, props.name]);

    const {
        isRecording,
        interimTranscript,
        startRecording,
        stopRecording,
        error: transcriptionError,
    } = useRealtimeTranscription({
        onFinalTranscript: handleFinalTranscript,
    });

    const handleToggleRecording = async () => {
        setLocalError(null);
        if (isRecording) {
            stopRecording();
        } else {
            try {
                await startRecording();
            } catch (err) {
                setLocalError(err.message || 'Failed to start recording');
            }
        }
    };

    const handleTextChange = (e) => {
        if (onChange) {
            onChange(e);
        }
    };

    // Display value with interim transcript when recording
    const displayValue = isRecording && interimTranscript
        ? `${value}${value ? ' ' : ''}${interimTranscript}`
        : value;

    const error = localError || transcriptionError;

    // Mic button component
    const MicButton = () => {
        if (!isDictationEnabled) {
            return (
                <Tooltip title="Dictation is disabled">
                    <span>
                        <IconButton disabled size="small">
                            <MicOffIcon color="disabled" />
                        </IconButton>
                    </span>
                </Tooltip>
            );
        }

        return (
            <Tooltip title={isRecording ? 'Stop dictation' : 'Start dictation (speech-to-text)'}>
                <IconButton
                    onClick={handleToggleRecording}
                    disabled={disabled}
                    color={isRecording ? 'error' : 'primary'}
                    size="small"
                    sx={{
                        backgroundColor: isRecording ? 'error.light' : 'transparent',
                        '&:hover': {
                            backgroundColor: isRecording ? 'error.main' : 'action.hover',
                        },
                    }}
                >
                    {isRecording ? <StopIcon /> : <MicIcon />}
                </IconButton>
            </Tooltip>
        );
    };

    return (
        <Box sx={{ position: 'relative', ...sx }}>
            <TextField
                value={displayValue}
                onChange={handleTextChange}
                multiline={multiline}
                rows={rows}
                label={label}
                placeholder={placeholder}
                disabled={disabled || isRecording}
                fullWidth
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <MicButton />
                                {isRecording && (
                                    <RecordingIndicator>
                                        <PulsingDot />
                                    </RecordingIndicator>
                                )}
                            </Box>
                        </InputAdornment>
                    ),
                }}
                {...props}
            />

            {isRecording && (
                <Typography
                    variant="caption"
                    color="error"
                    sx={{ display: 'block', mt: 0.5 }}
                >
                    Recording... Speak clearly into your microphone
                </Typography>
            )}

            {error && (
                <Chip
                    label={error}
                    color="error"
                    size="small"
                    onDelete={() => setLocalError(null)}
                    sx={{ mt: 0.5 }}
                />
            )}
        </Box>
    );
};

export default DictationTextField;
